import { useState } from "react";
import { Button, Col, Form, Input, message, Row } from "antd";
import { useGetData } from "../../../../Network/useGetQueryData";
import { useParams } from "react-router-dom";
import { usePostData } from "../../../../Network/usePostData";
import { usePutData } from "../../../../Network/usePutData";
import { SearchIcon } from "../../../../Icons/SearchIcon";
import { InfiniteSelect } from "../../../../components/infiniteSelect";
import { UserCards } from "../../UsersCard";
import { Loading } from "../../../../components/Loading";
import { useDeleteData } from "../../../../Network/useDeleteData";
import { useTranslation } from "react-i18next";
import "./style.scss";

export const UsersStep = ({
  onChangeStep,
}: {
  onChangeStep?: (step: number) => void;
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>();
  const [selectedBranch, setSelectedBranch] = useState<any>();
  const [userSearchKey, setUserSearchKey] = useState<any>();
  const [editView, setEditView] = useState<boolean>();
  const [deletedBranch, setDeletedBranch] = useState<any>();

  const { id } = useParams();
  const [form] = Form.useForm();

  const {
    data: users,
    isLoading,
    refetch,
  } = useGetData<{
    data: any;
  }>({
    queryKey: ["sponsor-users"],
    url: `user?sponsorId=${id}&offset=0&limit=30&withDeleted=false`,
    params: { searchKey: userSearchKey },
    options: {
      enabled: !!id,
      queryKey: ["sponsor-users", { userSearchKey }],
    },
  });

  const { mutateAsync } = usePostData({
    url: "user",
    options: {
      onSuccess: (data) => {
        form.resetFields();
        message.success("User Added Successfully");
      },
      onError: (error) => {
        //@ts-ignore
        message.error(error.response.data.message || "Something went wrong");
      },
    },
  });

  const { mutate } = usePutData<{ message: string }, { name: string }>({
    url: "user",
    options: {
      onSuccess: (data) => {
        form.resetFields();
        message.success("User Added Successfully");
      },
      onError: (error) => {
        //@ts-ignore
        message.error(error.response.data.message || "Something went wrong");
      },
    },
  });

  const { mutate: deleteItem } = useDeleteData({
    url: `user/${deletedBranch}`,
    options: {
      onSuccess: () => {
        message.success(t("messages.deleteSuccess"));
        setDeletedBranch("");
        refetch();
      },
      onError: (error) => {
        message.error(t("messages.deleteError"));
        console.error("Failed to delete item:", error);
      },
    },
  });

  const handleAddUser = async (values: any, goNext?: boolean) => {
    setLoading(true);
    try {
      const branchData = {
        ...values,
        countryCode: "+20",
        sponsorId: +id,
      };

      if (selectedBranch) mutate({ ...branchData, id: selectedBranch?.id });
      else {
        await mutateAsync(branchData);
        refetch();
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSetBranchToEdit = (branch: any) => {
    setSelectedBranch(branch);
    if (!editView) setEditView(true);
    form.setFieldsValue({
      ...branch,
    });
  };

  return (
    <>
      {editView && (
        <Form
          form={form}
          className="branch-form-data"
          layout="vertical"
          onFinish={handleAddUser}
        >
          <p className="form-section-title">{t("form.addUser")}</p>
          <Row gutter={32}>
            <Col xl={12}>
              <Form.Item label={t("form.firstName")} name="firstName">
                <Input />
              </Form.Item>
            </Col>
            <Col xl={12}>
              <Form.Item label={t("form.lastName")} name="lastName">
                <Input />
              </Form.Item>
            </Col>
            <Col xl={12}>
              <Form.Item
                label={t("form.password")}
                name="password"
                rules={[{ message: t("form.enterPassword") }]}
              >
                <Input.Password placeholder={t("form.enterPassword")} />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label={t("form.phone")} name="phoneNumber">
                <Input />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label={t("form.email")} name="email">
                <Input />
              </Form.Item>
            </Col>

            <Col xl={12}>
              <Form.Item label={t("form.role")} name="roleId">
                <InfiniteSelect
                  url="role"
                  optionsKeys={{ label: "name", value: "id" }}
                  params={{ withDeleted: false, offset: 0, limit: 20 }}
                  queryKey={["roleID"]}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="steps-action-footer">
            <Button loading={loading} type="primary" htmlType="submit">
              {t("form.save")}
            </Button>
          </div>
        </Form>
      )}

      <div className="branches-list">
        <div className="form-list-header">
          <p>{t("form.users")}</p>

          <div className="header-search">
            {!editView && (
              <Button onClick={() => setEditView(true)} type="primary">
                {t("form.add")}
              </Button>
            )}
            <Input
              size="large"
              placeholder={t("form.searchUsers")}
              prefix={<SearchIcon />}
              onChange={(e) => setUserSearchKey(e.target.value)}
            />
          </div>
        </div>
        <Row gutter={32}>
          {isLoading ? (
            <Loading />
          ) : !users?.data?.items?.length ? (
            <div
              style={{ textAlign: "center", margin: "20px 0", width: " 100%" }}
            >
              {t("messages.noBranches")}
            </div>
          ) : (
            users?.data?.items?.map((user) => (
              <Col key={user.id as number} xl={12}>
                <UserCards
                  isEdit={selectedBranch?.id === user.id}
                  onCardClick={handleSetBranchToEdit}
                  deleteItem={deleteItem}
                  onChangePrimary={async (data) => {
                    mutate({
                      ...data,
                      isPrimary: true,
                    });

                    setTimeout(() => {
                      refetch();
                    }, 500);
                  }}
                  data={user}
                />
              </Col>
            ))
          )}
        </Row>
      </div>
    </>
  );
};
